import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Video, Calendar, Play, User, Clock, Filter, ExternalLink } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from "@/contexts/RoleContext";
import { toast } from "sonner";
import { PageTour } from "@/components/tour/PageTour";
import { demoLibrarySteps } from "@/lib/tour-steps";

interface DemoRecording {
  id: string;
  title: string | null;
  fathom_share_url: string | null;
  duration_seconds: number | null;
  recorded_at: string;
  transcript_summary: string | null;
  demo_id: string | null;
  demos: {
    prospect_name: string;
    demo_date: string;
    status: string | null;
    outcome: string | null;
    deal_value: number | null;
  } | null;
  closers: {
    name: string;
  } | null;
}

type FilterType = "all" | "with_demo" | "completed";

function formatDuration(seconds: number | null): string {
  if (!seconds || seconds <= 0) return "--:--";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

export default function DemoLibrary() {
  const { clientId } = useRole();
  const [filter, setFilter] = useState<FilterType>("all");
  const [expandedSummary, setExpandedSummary] = useState<string | null>(null);

  const { data: recordings, isLoading } = useQuery({
    queryKey: ["client-demo-recordings", clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from("demo_recordings")
        .select("id, title, fathom_share_url, duration_seconds, recorded_at, transcript_summary, demo_id, demos(prospect_name, demo_date, status, outcome, deal_value), closers(name)")
        .order("recorded_at", { ascending: false });
      // RLS client_read_own_demo_recordings enforces client_id scoping
      if (error) {
        toast.error("Failed to load demo recordings");
        throw error;
      }
      return (data ?? []) as DemoRecording[];
    },
    enabled: !!clientId,
  });

  const filtered = useMemo(() => {
    if (!recordings) return [];
    switch (filter) {
      case "with_demo":
        return recordings.filter((r) => r.demo_id !== null);
      case "completed":
        return recordings.filter((r) => r.demos?.status === "completed");
      default:
        return recordings;
    }
  }, [recordings, filter]);

  // Stats
  const stats = useMemo(() => {
    if (!recordings) return { total: 0, withDemo: 0, completed: 0, totalHours: 0 };
    const withDemo = recordings.filter((r) => r.demo_id !== null).length;
    const completed = recordings.filter((r) => r.demos?.status === "completed").length;
    const totalSeconds = recordings.reduce((sum, r) => sum + (r.duration_seconds ?? 0), 0);
    return {
      total: recordings.length,
      withDemo,
      completed,
      totalHours: Math.round((totalSeconds / 3600) * 10) / 10,
    };
  }, [recordings]);

  const getOutcomeBadge = (outcome: string | null) => {
    switch (outcome) {
      case "closed":
        return <BadgeVenture variant="success">Closed Won</BadgeVenture>;
      case "lost":
        return <BadgeVenture variant="error">Lost</BadgeVenture>;
      case "pending":
        return <BadgeVenture variant="warning">Pending</BadgeVenture>;
      default:
        return <BadgeVenture variant="default">In Progress</BadgeVenture>;
    }
  };

  return (
    <>
      <PageTour steps={demoLibrarySteps} />
      <motion.div
        className="space-y-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Video className="w-6 h-6 text-secondary" />
              <h1 className="text-2xl font-bold">Demo Library</h1>
            </div>
            <p className="text-muted-foreground">View all your recorded demos and outcomes</p>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as FilterType)}
              className="px-4 py-2 border border-border rounded-lg bg-surface text-sm focus:outline-none focus:border-secondary"
            >
              <option value="all">All Recordings ({stats.total})</option>
              <option value="with_demo">With Demo ({stats.withDemo})</option>
              <option value="completed">Completed ({stats.completed})</option>
            </select>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <VentureCard>
            <VentureCardContent className="text-center py-4">
              <Video className="w-5 h-5 mx-auto text-secondary mb-2" />
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total Recordings</p>
            </VentureCardContent>
          </VentureCard>
          <VentureCard>
            <VentureCardContent className="text-center py-4">
              <Play className="w-5 h-5 mx-auto text-blue-500 mb-2" />
              <p className="text-2xl font-bold">{stats.withDemo}</p>
              <p className="text-xs text-muted-foreground">Linked to Demo</p>
            </VentureCardContent>
          </VentureCard>
          <VentureCard>
            <VentureCardContent className="text-center py-4">
              <Calendar className="w-5 h-5 mx-auto text-green-500 mb-2" />
              <p className="text-2xl font-bold">{stats.completed}</p>
              <p className="text-xs text-muted-foreground">Completed Demos</p>
            </VentureCardContent>
          </VentureCard>
          <VentureCard>
            <VentureCardContent className="text-center py-4">
              <Clock className="w-5 h-5 mx-auto text-amber-500 mb-2" />
              <p className="text-2xl font-bold">{stats.totalHours}h</p>
              <p className="text-xs text-muted-foreground">Total Recorded</p>
            </VentureCardContent>
          </VentureCard>
        </div>

        {/* Recording Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-64 rounded-lg bg-muted animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <VentureCard>
            <VentureCardContent className="py-12 text-center">
              <Video className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No demo recordings yet</h3>
              <p className="text-muted-foreground">
                Your demos will appear here once your closers sync their Fathom recordings.
              </p>
            </VentureCardContent>
          </VentureCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((rec) => (
              <motion.div
                key={rec.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2 }}
              >
                <VentureCard className="h-full hover:border-secondary transition-colors">
                  {/* Recording Thumbnail */}
                  <div className="relative h-32 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-t-lg flex items-center justify-center">
                    {rec.fathom_share_url ? (
                      <>
                        <div className="absolute inset-0 bg-black/20 rounded-t-lg" />
                        <button
                          onClick={() => window.open(rec.fathom_share_url!, "_blank", "noopener")}
                          className="relative z-10 w-14 h-14 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors"
                          aria-label="Watch recording"
                        >
                          <Play className="w-6 h-6 text-primary ml-1" />
                        </button>
                        {rec.duration_seconds && rec.duration_seconds > 0 && (
                          <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 rounded text-xs text-white">
                            {formatDuration(rec.duration_seconds)}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center">
                        <Video className="w-10 h-10 text-muted-foreground/50 mx-auto mb-2" />
                        <p className="text-xs text-muted-foreground">Recording processing...</p>
                      </div>
                    )}
                  </div>

                  <VentureCardContent className="space-y-3">
                    {/* Title */}
                    <h3 className="font-semibold">{rec.title || "Demo Recording"}</h3>

                    {/* Closer */}
                    {rec.closers?.name && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <User className="w-4 h-4" />
                        <span>Closer: {rec.closers.name}</span>
                      </div>
                    )}

                    {/* Linked Demo Info */}
                    {rec.demos && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {rec.demos.prospect_name} - {format(new Date(rec.demos.demo_date), "MMM d, yyyy")}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {getOutcomeBadge(rec.demos.outcome)}
                        </div>
                      </div>
                    )}

                    {/* Recorded date */}
                    <div className="text-xs text-muted-foreground">
                      Recorded {format(new Date(rec.recorded_at), "MMM d, yyyy 'at' h:mm a")}
                    </div>

                    {/* Transcript Summary */}
                    {rec.transcript_summary && (
                      <div className="pt-2 border-t border-border">
                        <p className="text-sm text-muted-foreground">
                          {expandedSummary === rec.id
                            ? rec.transcript_summary
                            : rec.transcript_summary.slice(0, 150)}
                          {rec.transcript_summary.length > 150 && (
                            <button
                              onClick={() =>
                                setExpandedSummary(expandedSummary === rec.id ? null : rec.id)
                              }
                              className="ml-1 text-secondary hover:underline text-sm font-medium"
                            >
                              {expandedSummary === rec.id ? "Show less" : "Read more"}
                            </button>
                          )}
                        </p>
                      </div>
                    )}

                    {/* Actions */}
                    {rec.fathom_share_url ? (
                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="default"
                          size="sm"
                          className="flex-1 gap-2"
                          onClick={() => window.open(rec.fathom_share_url!, "_blank", "noopener")}
                        >
                          <Play className="w-4 h-4" />
                          Watch Recording
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          title="Open in Fathom"
                          onClick={() => window.open(rec.fathom_share_url!, "_blank", "noopener")}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button variant="outline" size="sm" className="w-full" disabled>
                        <Video className="w-4 h-4 mr-2" />
                        Recording processing...
                      </Button>
                    )}
                  </VentureCardContent>
                </VentureCard>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </>
  );
}
